import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Image from '../../components/AppImage';
import { coachService } from '../../services/coachService';
import { useAuth } from '../../contexts/AuthContext';
import { getRegionLabel } from '../../utils/tanzaniaRegions';
import { formatValidityMonthsLabel } from '../../utils/coachLicense';
import { COACH_GRADE_CATEGORY_CONFIG, getCoachGradeLabel } from '../../constants/coachGrades';
import { getNationalityLabel } from '../../data/nationalities';
import EntityFinancialRecordsPanel from '../../components/finance/EntityFinancialRecordsPanel';
import { useCoachEmailCheck } from '../../hooks/useCoachEmailCheck';
import QuickAnalyticsPanel from '../../components/analytics/QuickAnalyticsPanel';
import { computeCoachAnalytics } from '../../utils/leaguePageAnalytics';

const CATEGORY_CONFIG = COACH_GRADE_CATEGORY_CONFIG;

const SORTED_CATEGORY_CONFIG = [...COACH_GRADE_CATEGORY_CONFIG].sort(
  (a, b) => (a.order ?? 999) - (b.order ?? 999)
);

const CoachActionMenu = ({
  coach,
  isAdmin,
  isUpdating,
  isDeleting,
  onViewProfile,
  onEdit,
  onViewCertificate,
  onViewPassport,
  onToggleStatus,
  onDelete,
}) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  if (!isAdmin) return null;

  const closeMenu = () => setOpen(false);
  const runAction = (action) => {
    closeMenu();
    action();
  };

  const isActive = coach.licenseStatus === 'active';
  const certificates = (coach.coachingCertificates || []).filter((item) => item.fileUrl);
  const hasPassport = !!coach.passport?.fileUrl;

  return (
    <div className="relative" ref={menuRef}>
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        onClick={() => setOpen((prev) => !prev)}
        title="Coach actions"
        disabled={isUpdating || isDeleting}
      >
        <Icon name="MoreVertical" size={16} className="text-muted-foreground hover:text-primary" />
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 min-w-[200px] bg-card border border-border rounded-lg shadow-lg py-1">
          <button
            type="button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted/60 transition-colors"
            onClick={() => runAction(() => onViewProfile(coach))}
          >
            <Icon name="Eye" size={14} />
            View Profile
          </button>
          <button
            type="button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted/60 transition-colors"
            onClick={() => runAction(() => onEdit(coach))}
          >
            <Icon name="Edit" size={14} />
            Update Coach
          </button>
          {certificates.length > 0 && (
            <button
              type="button"
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted/60 transition-colors"
              onClick={() => runAction(() => onViewCertificate(coach))}
            >
              <Icon name="FileText" size={14} />
              View Certificate{certificates.length > 1 ? 's' : ''}
            </button>
          )}
          {hasPassport && (
            <button
              type="button"
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted/60 transition-colors"
              onClick={() => runAction(() => onViewPassport(coach))}
            >
              <Icon name="BookOpen" size={14} />
              View Passport
            </button>
          )}
          <button
            type="button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted/60 transition-colors disabled:opacity-50"
            onClick={() => runAction(() => onToggleStatus(coach))}
            disabled={isUpdating}
          >
            <Icon name={isActive ? 'Ban' : 'CheckCircle'} size={14} />
            {isUpdating ? 'Updating...' : isActive ? 'Suspend License' : 'Activate License'}
          </button>
          <div className="my-1 border-t border-border" />
          <button
            type="button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
            onClick={() => runAction(() => onDelete(coach))}
            disabled={isDeleting}
          >
            <Icon name="Trash2" size={14} />
            {isDeleting ? 'Deleting...' : 'Delete Coach'}
          </button>
        </div>
      )}
    </div>
  );
};

const LeagueCoaches = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'staff';
  const isSystemAdmin = userProfile?.role === 'admin';
  const publicRegistrationUrl = useMemo(() => coachService.getPublicRegistrationUrl(), []);
  const { emailStatus: inviteEmailStatus, scheduleEmailCheck, resetEmailStatus, checkEmail } = useCoachEmailCheck();

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [selectedCoachForPhoto, setSelectedCoachForPhoto] = useState(null);
  const [selectedCoachForProfile, setSelectedCoachForProfile] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showPublicLinkModal, setShowPublicLinkModal] = useState(false);
  const [publicLinkCopied, setPublicLinkCopied] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteCoachName, setInviteCoachName] = useState('');
  const [inviteExpiresInDays, setInviteExpiresInDays] = useState('7');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [inviteSuccess, setInviteSuccess] = useState(null);
  const [generatedLink, setGeneratedLink] = useState('');
  const [coachToDelete, setCoachToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [statusUpdatingIds, setStatusUpdatingIds] = useState(new Set());
  const [deletingCoachIds, setDeletingCoachIds] = useState(new Set());
  const [actionMessage, setActionMessage] = useState(null);
  const [actionError, setActionError] = useState(null);

  const loadCoaches = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await coachService.getAll();
      setCoaches(data || []);
    } catch (err) {
      setError(err.message || 'Failed to load coaches');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCoaches();
  }, []);

  useEffect(() => {
    const coachId = searchParams.get('coach');
    if (!coachId || isLoading || !coaches.length) return;

    const coach = coaches.find((item) => item.id === coachId);
    if (!coach) return;

    openProfileModal(coach);
    setSearchParams({}, { replace: true });
  }, [coaches, isLoading, searchParams, setSearchParams]);

  const filteredCoaches = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return coaches;
    return coaches.filter((coach) =>
      [
        coach.coachName,
        coach.email,
        coach.mobile,
        coach.licenseNumber,
        coach.grade,
        getRegionLabel(coach.region),
        getNationalityLabel(coach.nationality),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [coaches, search]);

  const groupedCoaches = useMemo(() => {
    const knownGrades = CATEGORY_CONFIG.map((category) => category.key).filter((key) => key !== 'Other');
    return CATEGORY_CONFIG.reduce((acc, category) => {
      acc[category.key] = filteredCoaches.filter((coach) => {
        if (category.key === 'Other') {
          return !knownGrades.includes(coach.grade);
        }
        return coach.grade === category.key;
      });
      return acc;
    }, {});
  }, [filteredCoaches]);

  const coachAnalytics = useMemo(() => computeCoachAnalytics(coaches), [coaches]);

  const breadcrumbItems = [
    { label: 'Home', path: '/admin-dashboard' },
    { label: 'League Management', path: '/league-management' },
    { label: 'League Coaches' },
  ];

  const getLicenseStatusClass = (status) => {
    if (status === 'active') return 'text-success';
    if (status === 'expired') return 'text-destructive';
    if (status === 'suspended') return 'text-warning';
    return 'text-muted-foreground';
  };

  const openPhotoModal = (coach) => {
    setSelectedCoachForPhoto(coach);
    setPhotoModalOpen(true);
  };

  const closePhotoModal = () => {
    setPhotoModalOpen(false);
    setSelectedCoachForPhoto(null);
  };

  const openProfileModal = (coach) => {
    setSelectedCoachForProfile(coach);
    setProfileModalOpen(true);
  };

  const closeProfileModal = () => {
    setProfileModalOpen(false);
    setSelectedCoachForProfile(null);
  };

  const handleSendInvite = async () => {
    setInviteLoading(true);
    setInviteError(null);
    setInviteSuccess(null);
    setGeneratedLink('');
    try {
      const emailCheck = await checkEmail(inviteEmail);
      if (emailCheck.available === false) {
        setInviteError(emailCheck.message || 'This email is already registered to a coach.');
        return;
      }

      const result = await coachService.createSelfRegistrationInvite({
        email: inviteEmail,
        coachName: inviteCoachName,
        expiresInDays: Number(inviteExpiresInDays) || 7,
      });
      setGeneratedLink(result.registrationUrl);
      setInviteSuccess(
        `Registration link sent to ${inviteEmail}. The link expires after submission or on ${new Date(result.expiresAt).toLocaleDateString()}.`
      );
    } catch (err) {
      setInviteError(err.message || 'Failed to send registration link');
    } finally {
      setInviteLoading(false);
    }
  };

  const copyGeneratedLink = async () => {
    if (!generatedLink) return;
    try {
      await navigator.clipboard.writeText(generatedLink);
      setInviteSuccess('Registration link copied to clipboard.');
    } catch {
      setInviteError('Could not copy link. Please copy it manually.');
    }
  };

  const copyPublicRegistrationLink = async () => {
    try {
      await navigator.clipboard.writeText(publicRegistrationUrl);
      setPublicLinkCopied(true);
    } catch {
      setPublicLinkCopied(false);
    }
  };

  const closeInviteModal = () => {
    setShowInviteModal(false);
    setInviteEmail('');
    setInviteCoachName('');
    resetEmailStatus();
    setInviteError(null);
    setInviteSuccess(null);
    setGeneratedLink('');
  };

  const handleEditCoach = (coach) => {
    navigate(`/coach-registration?edit=${coach.id}`);
  };

  const handleViewCertificate = (coach) => {
    const certificates = (coach.coachingCertificates || []).filter((item) => item.fileUrl);
    if (certificates.length === 1) {
      window.open(certificates[0].fileUrl, '_blank');
      return;
    }
    if (certificates.length > 1) {
      openProfileModal(coach);
    }
  };

  const handleViewPassport = (coach) => {
    if (coach.passport?.fileUrl) {
      window.open(coach.passport.fileUrl, '_blank');
    }
  };

  const handleToggleLicenseStatus = async (coach) => {
    if (!coach?.id) return;
    const nextStatus = coach.licenseStatus === 'active' ? 'suspended' : 'active';

    setStatusUpdatingIds((prev) => new Set(prev).add(coach.id));
    setActionError(null);
    setActionMessage(null);

    try {
      const updatedCoach = await coachService.update(coach.id, { licenseStatus: nextStatus });
      setCoaches((prev) => prev.map((item) => (item.id === coach.id ? updatedCoach : item)));
      setActionMessage(
        `${coach.coachName || 'Coach'} license ${nextStatus === 'active' ? 'activated' : 'suspended'}.`
      );
      if (selectedCoachForProfile?.id === coach.id) {
        setSelectedCoachForProfile(updatedCoach);
      }
    } catch (err) {
      setActionError(err.message || 'Failed to update coach license status.');
    } finally {
      setStatusUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(coach.id);
        return next;
      });
    }
  };

  const handleRequestDeleteCoach = (coach) => {
    setCoachToDelete(coach);
  };

  const handleConfirmDeleteCoach = async () => {
    if (!coachToDelete?.id) return;

    const coachId = coachToDelete.id;
    setDeleteLoading(true);
    setDeletingCoachIds((prev) => new Set(prev).add(coachId));
    setActionError(null);
    setActionMessage(null);

    try {
      await coachService.delete(coachId);
      setCoaches((prev) => prev.filter((item) => item.id !== coachId));
      setActionMessage(`${coachToDelete.coachName || 'Coach'} deleted successfully.`);
      if (selectedCoachForProfile?.id === coachId) {
        closeProfileModal();
      }
      setCoachToDelete(null);
    } catch (err) {
      setActionError(err.message || 'Failed to delete coach.');
    } finally {
      setDeleteLoading(false);
      setDeletingCoachIds((prev) => {
        const next = new Set(prev);
        next.delete(coachId);
        return next;
      });
    }
  };

  const renderCoachActions = (coach) => (
    <CoachActionMenu
      coach={coach}
      isAdmin={isAdmin}
      isUpdating={statusUpdatingIds.has(coach.id)}
      isDeleting={deletingCoachIds.has(coach.id) || (deleteLoading && coachToDelete?.id === coach.id)}
      onViewProfile={openProfileModal}
      onEdit={handleEditCoach}
      onViewCertificate={handleViewCertificate}
      onViewPassport={handleViewPassport}
      onToggleStatus={handleToggleLicenseStatus}
      onDelete={handleRequestDeleteCoach}
    />
  );

  const getCoachPhoto = (coach) => coach.coachPhoto?.fileUrl || '/assets/images/no_image.png';

  const renderCoachCard = (coach) => (
    <div
      key={coach.id}
      className="bg-card border border-border rounded-lg p-4 space-y-3 hover:border-primary/50 transition-colors relative"
    >
      <div className="absolute top-2 right-2">
        {renderCoachActions(coach)}
      </div>

      <div className="flex items-center space-x-3">
        <button
          type="button"
          className="w-14 h-14 rounded-full overflow-hidden border border-border focus:outline-none focus:ring-2 focus:ring-primary/40"
          onClick={() => openPhotoModal(coach)}
          title="Click to view full photo"
        >
          <Image src={getCoachPhoto(coach)} alt={coach.coachName || 'Coach'} className="w-full h-full object-cover" />
        </button>
        <div className="flex-1 min-w-0">
          <button
            type="button"
            className="text-sm font-semibold text-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40 text-left"
            onClick={() => openProfileModal(coach)}
            title="Click to view full profile"
          >
            {coach.coachName || 'Unknown Coach'}
          </button>
          <p className="text-xs text-muted-foreground">
            {getCoachGradeLabel(coach.grade)}
          </p>
        </div>
        <span className={`text-xs font-medium capitalize ${getLicenseStatusClass(coach.licenseStatus)}`}>
          {coach.licenseStatus || 'pending'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="space-y-3">
          <div>
            <p className="text-muted-foreground">License</p>
            <p className="font-medium text-foreground">{coach.licenseNumber || 'N/A'}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Email</p>
            <p className="font-medium text-foreground truncate">{coach.email || 'N/A'}</p>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <p className="text-muted-foreground">Validity</p>
            <p className="font-medium text-foreground">
              {formatValidityMonthsLabel(coach.validityPeriod)}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">Mobile</p>
            <p className="font-medium text-foreground">{coach.mobile || 'N/A'}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCoachListItem = (coach) => (
    <div
      key={coach.id}
      className="bg-card border border-border rounded-lg p-4 hover:border-primary/50 transition-colors"
    >
      <div className="flex items-center gap-4">
        <button
          type="button"
          className="w-16 h-16 rounded-full overflow-hidden border border-border flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-primary/40"
          onClick={() => openPhotoModal(coach)}
          title="Click to view full photo"
        >
          <Image src={getCoachPhoto(coach)} alt={coach.coachName || 'Coach'} className="w-full h-full object-cover" />
        </button>

        <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
          <div className="md:col-span-2">
            <button
              type="button"
              className="text-sm font-semibold text-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40 text-left"
              onClick={() => openProfileModal(coach)}
              title="Click to view full profile"
            >
              {coach.coachName || 'Unknown Coach'}
            </button>
            <p className="text-xs text-muted-foreground">{getCoachGradeLabel(coach.grade)}</p>
          </div>

          <div>
            <p className="text-xs text-muted-foreground">License</p>
            <p className="text-sm font-medium text-foreground">{coach.licenseNumber || 'N/A'}</p>
          </div>

          <div>
            <p className="text-xs text-muted-foreground">Validity</p>
            <p className="text-sm font-medium text-foreground">
              {formatValidityMonthsLabel(coach.validityPeriod)}
            </p>
          </div>

          <div className="flex items-center gap-2 justify-end">
            <span className={`text-xs font-medium capitalize ${getLicenseStatusClass(coach.licenseStatus)}`}>
              {coach.licenseStatus || 'pending'}
            </span>
            {renderCoachActions(coach)}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />
        <main
          className={`pt-16 transition-all duration-300 ${
            isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
          }`}
        >
          <div className="p-6 space-y-6">
            <Breadcrumb items={breadcrumbItems} />

            <QuickAnalyticsPanel
              title="Coaches Overview"
              description="Quick snapshot of licenses, approvals, team assignments, grades, and regional spread."
              stats={coachAnalytics.stats}
              isLoading={isLoading}
              breakdownSections={[
                { title: 'License Status', items: coachAnalytics.licenseBreakdown, chartType: 'donut' },
                { title: 'Registration Status', items: coachAnalytics.registrationBreakdown, chartType: 'donut' },
                { title: 'Top Grades', items: coachAnalytics.gradeBreakdown },
                { title: 'Top Regions', items: coachAnalytics.regionBreakdown },
              ]}
            />

            <div className="flex flex-col space-y-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Users" size={24} className="text-primary" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-foreground">League Coaches</h1>
                    <p className="text-muted-foreground">
                      View and manage registered basketball coaches
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                    <Button
                      variant={viewMode === 'grid' ? 'default' : 'ghost'}
                      size="sm"
                      className="h-8 px-3"
                      onClick={() => setViewMode('grid')}
                      title="Grid View"
                    >
                      <Icon name="Grid3x3" size={16} />
                    </Button>
                    <Button
                      variant={viewMode === 'list' ? 'default' : 'ghost'}
                      size="sm"
                      className="h-8 px-3"
                      onClick={() => setViewMode('list')}
                      title="List View"
                    >
                      <Icon name="List" size={16} />
                    </Button>
                  </div>
                  <Button
                    variant="outline"
                    iconName="Link"
                    onClick={() => {
                      setShowInviteModal(true);
                      setInviteError(null);
                      setInviteSuccess(null);
                      setGeneratedLink('');
                      resetEmailStatus();
                    }}
                  >
                    Send Self-Registration Link
                  </Button>
                  {isSystemAdmin && (
                    <Button
                      variant="outline"
                      iconName="Globe"
                      onClick={() => {
                        setShowPublicLinkModal(true);
                        setPublicLinkCopied(false);
                      }}
                    >
                      Public Registration Link
                    </Button>
                  )}
                  <Button
                    variant="default"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={() => navigate('/coach-registration')}
                  >
                    Register New Coach
                  </Button>
                </div>
              </div>
            </div>

            <div className="max-w-md">
              <Input
                label="Search Coaches"
                placeholder="Search by name, email, license, or region"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {actionMessage && (
              <div className="px-4 py-3 rounded-lg border bg-success/10 border-success/20 text-success text-sm">
                {actionMessage}
              </div>
            )}

            {actionError && (
              <div className="px-4 py-3 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive text-sm">
                {actionError}
              </div>
            )}

            {showInviteModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                <div className="bg-card border border-border rounded-lg w-full max-w-lg p-6 space-y-4 card-shadow">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-foreground">Coach Self-Registration Link</h2>
                    <Button variant="ghost" size="sm" onClick={closeInviteModal}>
                      <Icon name="X" size={16} />
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Generate a single-use registration link and email it to the coach. The link expires
                    immediately after they submit their registration.
                  </p>
                  {inviteError && (
                    <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">
                      {inviteError}
                    </div>
                  )}
                  {inviteSuccess && (
                    <div className="text-sm text-success bg-success/10 border border-success/20 rounded-lg px-3 py-2">
                      {inviteSuccess}
                    </div>
                  )}
                  <div>
                    <Input
                      label="Coach Email"
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => {
                        setInviteEmail(e.target.value);
                        setInviteError(null);
                        scheduleEmailCheck(e.target.value);
                      }}
                      placeholder="coach@example.com"
                      required
                      error={
                        inviteEmailStatus.available === false ? inviteEmailStatus.message : undefined
                      }
                    />
                    {inviteEmailStatus.checking && (
                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                        <Icon name="Loader2" size={12} className="animate-spin" />
                        Checking for duplicate email...
                      </p>
                    )}
                    {!inviteEmailStatus.checking && inviteEmailStatus.available === true && inviteEmail.trim() && (
                      <p className="text-xs text-success mt-1">
                        {inviteEmailStatus.message || 'Email is available for registration.'}
                      </p>
                    )}
                  </div>
                  <Input
                    label="Coach Name (optional)"
                    value={inviteCoachName}
                    onChange={(e) => setInviteCoachName(e.target.value)}
                    placeholder="Prefill coach name on the form"
                  />
                  <Input
                    label="Link Expires After (days if unused)"
                    type="number"
                    min="1"
                    max="30"
                    value={inviteExpiresInDays}
                    onChange={(e) => setInviteExpiresInDays(e.target.value)}
                  />
                  {generatedLink && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">Generated Link</label>
                      <div className="flex gap-2">
                        <Input value={generatedLink} readOnly />
                        <Button type="button" variant="outline" onClick={copyGeneratedLink}>
                          Copy
                        </Button>
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={closeInviteModal} disabled={inviteLoading}>
                      Close
                    </Button>
                    <Button
                      loading={inviteLoading}
                      iconName="Send"
                      onClick={handleSendInvite}
                      disabled={
                        inviteLoading ||
                        inviteEmailStatus.checking ||
                        inviteEmailStatus.available === false ||
                        !inviteEmail.trim()
                      }
                    >
                      Send Link
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {showPublicLinkModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                <div className="bg-card border border-border rounded-lg w-full max-w-lg p-6 space-y-4 card-shadow">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-foreground">Public Coach Registration Link</h2>
                    <Button variant="ghost" size="sm" onClick={() => setShowPublicLinkModal(false)}>
                      <Icon name="X" size={16} />
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Share this general registration URL with coaches. Anyone with the link can register.
                    Duplicate emails are blocked during entry and on submission.
                  </p>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Registration URL</label>
                    <div className="flex gap-2">
                      <Input value={publicRegistrationUrl} readOnly />
                      <Button type="button" variant="outline" onClick={copyPublicRegistrationLink}>
                        Copy
                      </Button>
                    </div>
                    {publicLinkCopied && (
                      <p className="text-xs text-success">Public registration link copied to clipboard.</p>
                    )}
                  </div>
                  <div className="flex justify-end pt-2">
                    <Button variant="outline" onClick={() => setShowPublicLinkModal(false)}>
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {isLoading ? (
              <div className="flex justify-center py-12">
                <Icon name="Loader2" size={32} className="text-primary animate-spin" />
              </div>
            ) : error ? (
              <div className="px-4 py-3 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive">
                <div className="flex items-start gap-3">
                  <Icon name="AlertTriangle" size={18} />
                  <div>
                    <p className="font-medium">Error loading coaches</p>
                    <p className="text-sm">{error}</p>
                  </div>
                </div>
              </div>
            ) : filteredCoaches.length === 0 ? (
              <div className="bg-muted/30 border border-dashed border-border rounded-lg p-6 text-center">
                <div className="flex flex-col items-center space-y-2">
                  <Icon name="Users" size={24} className="text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">No coaches found.</p>
                </div>
              </div>
            ) : (
              <div className="space-y-8">
                {SORTED_CATEGORY_CONFIG.map((category) => {
                  const categoryCoaches = groupedCoaches[category.key] || [];
                  if (!categoryCoaches.length) return null;

                  return (
                    <div key={category.key} className="space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div>
                          <div className="flex items-center space-x-2">
                            <Icon name={category.icon} size={18} className="text-primary" />
                            <h2 className="text-xl font-semibold text-foreground">{category.label}</h2>
                          </div>
                          <p className="text-sm text-muted-foreground">{category.description}</p>
                        </div>
                        <span className="text-sm text-primary font-medium">
                          {categoryCoaches.length} {categoryCoaches.length === 1 ? 'coach' : 'coaches'}
                        </span>
                      </div>

                      {viewMode === 'grid' ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                          {categoryCoaches.map(renderCoachCard)}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {categoryCoaches.map(renderCoachListItem)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>

      {photoModalOpen && selectedCoachForPhoto && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={closePhotoModal}>
          <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 z-10 bg-black/40 hover:bg-black/60 text-white"
              onClick={closePhotoModal}
            >
              <Icon name="X" size={20} />
            </Button>
            <Image
              src={getCoachPhoto(selectedCoachForPhoto)}
              alt={selectedCoachForPhoto?.coachName || 'Coach photo'}
              className="w-full h-full object-contain rounded-lg bg-black"
            />
          </div>
        </div>
      )}

      {profileModalOpen && selectedCoachForProfile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={closeProfileModal}>
          <div
            className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto card-shadow"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  {selectedCoachForProfile?.coachName || 'Coach Profile'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {getCoachGradeLabel(selectedCoachForProfile?.grade)} • {getRegionLabel(selectedCoachForProfile?.region)}
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={closeProfileModal}>
                <Icon name="X" size={18} />
              </Button>
            </div>
            <div className="p-6 space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="w-32 h-32 rounded-full overflow-hidden border-2 border-border mx-auto sm:mx-0">
                  <Image
                    src={getCoachPhoto(selectedCoachForProfile)}
                    alt={selectedCoachForProfile?.coachName || 'Coach'}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">License Status</p>
                    <p className="text-base font-medium text-foreground capitalize">
                      {selectedCoachForProfile?.licenseStatus || 'N/A'}
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">License Number</p>
                      <p className="text-base font-medium text-foreground">
                        {selectedCoachForProfile?.licenseNumber || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Validity</p>
                      <p className="text-base font-medium text-foreground">
                        {formatValidityMonthsLabel(selectedCoachForProfile?.validityPeriod)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-muted/30 rounded-lg border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Mail" size={16} className="text-primary" />
                    <span className="text-sm font-medium text-foreground">Email</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{selectedCoachForProfile?.email || 'N/A'}</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Phone" size={16} className="text-primary" />
                    <span className="text-sm font-medium text-foreground">Mobile</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{selectedCoachForProfile?.mobile || 'N/A'}</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Globe" size={16} className="text-primary" />
                    <span className="text-sm font-medium text-foreground">Nationality</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {getNationalityLabel(selectedCoachForProfile?.nationality) || 'N/A'}
                  </p>
                </div>
              </div>

              <div className="p-4 bg-muted/30 rounded-lg border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Calendar" size={16} className="text-primary" />
                  <span className="text-sm font-medium text-foreground">License Expiration</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {selectedCoachForProfile?.licenseExpirationDate
                    ? new Date(selectedCoachForProfile.licenseExpirationDate).toLocaleDateString()
                    : 'N/A'}
                </p>
              </div>

              {selectedCoachForProfile?.teamAssignments?.length > 0 && (
                <div className="p-4 bg-muted/30 rounded-lg border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Users" size={16} className="text-primary" />
                    <span className="text-sm font-medium text-foreground">Team Assignments</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedCoachForProfile.teamAssignments.map((assignment) => (
                      <span
                        key={assignment.id}
                        className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary"
                      >
                        {assignment.regionalTeam?.name || assignment.team?.name || 'Team'} ({assignment.roleTitle})
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <EntityFinancialRecordsPanel
                compact
                entityType="coach"
                entityId={selectedCoachForProfile?.id}
                entityName={selectedCoachForProfile?.coachName}
                region={selectedCoachForProfile?.region}
                title="Coach License & Payment Records"
              />

              {(selectedCoachForProfile?.coachingCertificates?.length > 0 || selectedCoachForProfile?.passport?.fileUrl) && (
                <div className="p-4 bg-muted/30 rounded-lg border border-border space-y-4">
                  <div className="flex items-center gap-2">
                    <Icon name="FileText" size={16} className="text-primary" />
                    <span className="text-sm font-medium text-foreground">Documents</span>
                  </div>

                  {selectedCoachForProfile?.coachingCertificates?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Coaching Certificates
                      </p>
                      {selectedCoachForProfile.coachingCertificates.map((certificate) => (
                        <div key={certificate.id} className="flex items-center justify-between gap-3">
                          <span className="text-sm text-foreground truncate">{certificate.fileName || 'Certificate'}</span>
                          {certificate.fileUrl && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(certificate.fileUrl, '_blank')}
                            >
                              View
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedCoachForProfile?.passport?.fileUrl && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Passport / ID
                      </p>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm text-foreground truncate">
                          {selectedCoachForProfile.passport.fileName || 'Passport'}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewPassport(selectedCoachForProfile)}
                        >
                          View
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {isAdmin && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Edit"
                    onClick={() => {
                      closeProfileModal();
                      handleEditCoach(selectedCoachForProfile);
                    }}
                  >
                    Update Coach
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName={selectedCoachForProfile?.licenseStatus === 'active' ? 'Ban' : 'CheckCircle'}
                    onClick={() => handleToggleLicenseStatus(selectedCoachForProfile)}
                    loading={statusUpdatingIds.has(selectedCoachForProfile.id)}
                  >
                    {selectedCoachForProfile?.licenseStatus === 'active' ? 'Suspend License' : 'Activate License'}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    iconName="Trash2"
                    onClick={() => {
                      closeProfileModal();
                      handleRequestDeleteCoach(selectedCoachForProfile);
                    }}
                  >
                    Delete Coach
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {coachToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-md p-6 space-y-4 card-shadow">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
                <Icon name="AlertTriangle" size={18} className="text-destructive" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Delete Coach</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Are you sure you want to delete{' '}
                  <span className="font-medium text-foreground">{coachToDelete.coachName || 'this coach'}</span>?
                  This will remove their registration, documents, and team assignments. This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCoachToDelete(null)} disabled={deleteLoading}>
                Cancel
              </Button>
              <Button variant="destructive" loading={deleteLoading} iconName="Trash2" onClick={handleConfirmDeleteCoach}>
                Delete Coach
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LeagueCoaches;
